import type {
  NewsReportSubscription,
  NewsReportSubscriptionDeliveryFormat,
  NewsReportSubscriptionFrequency,
  NewsReportSubscriptionNotificationType,
  NewsReportSubscriptionScheduleConfig,
  NewsReportSubscriptionStatus,
} from "./api";

export const NEWS_REPORT_TIMEZONE = "Asia/Tokyo";

export const WEEKDAY_OPTIONS = [
  { value: 1, label: "月曜日" },
  { value: 2, label: "火曜日" },
  { value: 3, label: "水曜日" },
  { value: 4, label: "木曜日" },
  { value: 5, label: "金曜日" },
  { value: 6, label: "土曜日" },
  { value: 7, label: "日曜日" },
] as const;

export const MONTHLY_DAY_OPTIONS = Array.from({ length: 28 }, (_, index) => index + 1);

export type MonthlyScheduleInput =
  | { monthlyMode: "day_of_month"; monthlyDayOfMonth: number }
  | { monthlyMode: "last_day"; monthlyDayOfMonth?: never };

export type NewsReportScheduleInput =
  | { frequency: "daily"; weeklyDayOfWeek?: never; monthly?: never }
  | { frequency: "weekly"; weeklyDayOfWeek: number; monthly?: never }
  | { frequency: "monthly"; weeklyDayOfWeek?: never; monthly: MonthlyScheduleInput };

export function createNewsReportScheduleConfig(input: NewsReportScheduleInput): NewsReportSubscriptionScheduleConfig {
  if (input.frequency === "daily") {
    return { type: "daily" };
  }

  if (input.frequency === "weekly") {
    const dayOfWeek = clampInteger(input.weeklyDayOfWeek, 1, 7);
    return { type: "weekly", days_of_week: [dayOfWeek] };
  }

  if (input.monthly.monthlyMode === "last_day") {
    return { type: "monthly", monthly_mode: "last_day" };
  }

  const dayOfMonth = clampInteger(input.monthly.monthlyDayOfMonth, 1, 28);
  return {
    type: "monthly",
    monthly_mode: "day_of_month",
    days_of_month: [dayOfMonth],
  };
}

export function resolveNewsReportDeliveryFormat(
  notificationType: NewsReportSubscriptionNotificationType,
  includeAudio: boolean
): NewsReportSubscriptionDeliveryFormat {
  return notificationType === "email" && includeAudio ? "text_and_audio" : "text";
}

export function getNewsReportModeLabel(mode: NewsReportSubscription["mode"]) {
  return mode === "report" ? "レポート" : "ニュース";
}

export function getNewsReportFrequencyLabel(frequency: NewsReportSubscriptionFrequency) {
  switch (frequency) {
    case "weekly":
      return "毎週";
    case "monthly":
      return "毎月";
    case "daily":
    default:
      return "毎日";
  }
}

export function getNewsReportStatusLabel(status: NewsReportSubscriptionStatus) {
  switch (status) {
    case "paused":
      return "停止中";
    case "disabled":
      return "無効";
    case "active":
    default:
      return "有効";
  }
}

export function getNewsReportNotificationLabel(notificationType: NewsReportSubscriptionNotificationType) {
  switch (notificationType) {
    case "email":
      return "メール";
    case "google_chat":
      return "Google Chat";
    case "line":
    default:
      return "LINE";
  }
}

export function getNewsReportDeliveryFormatLabel(format: NewsReportSubscriptionDeliveryFormat) {
  return format === "text_and_audio" ? "音声付きメール" : "テキスト";
}

export function formatNewsReportSchedule(subscription: NewsReportSubscription) {
  const config = subscription.scheduleConfig;

  if (!config) return getNewsReportFrequencyLabel(subscription.frequency);

  if (config.type === "daily") {
    return "毎日";
  }

  if (config.type === "weekly") {
    const day = config.days_of_week[0];
    const label = WEEKDAY_OPTIONS.find((option) => option.value === day)?.label ?? "月曜日";
    return `毎週 ${label}`;
  }

  if (config.monthly_mode === "last_day") {
    return "毎月 月末";
  }

  const day = config.days_of_month[0] ?? 1;
  return `毎月 ${day}日`;
}

export function formatNewsReportDateTime(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ja-JP", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function clampInteger(value: number, min: number, max: number) {
  const normalized = Number(value);
  if (!Number.isInteger(normalized)) return min;
  return Math.min(Math.max(normalized, min), max);
}
